import { FC, useEffect, useState } from 'react'
import type { Product } from '@commerce/types/product'
import { usePriceWithOptionSelections } from '@lib/hooks/usePriceWithOptionSelections'
import { Text, useUI } from '@components/ui'
import { Container, Typography } from '@components/design'
import Storyblok, { StoryblokContent } from '@lib/storyblok'
import { ProductContent } from 'types/storyblok'
import CallToAction from './CallToAction'
import { useAddItem } from '@framework/cart'
import { ProductOptions } from '@components/product'
import {
  getProductVariant,
  selectDefaultOptionFromProduct,
  SelectedOptions,
} from '../helpers'
import { CartItemBody } from '@commerce/types/cart'
import { DynamicComponent } from '@components/common'
import { trackAddToCart } from '@lib/datalayer'
import InnerImageZoom from 'react-inner-image-zoom'
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css'
import ImageSlider from './ImageSlider'
import useUser from '@lib/hooks/useUser'
import toast from 'react-hot-toast'
import { isFuture } from 'date-fns'
import { PHARMA_SKUS } from '@lib/productCatalog'
import ProductSchema from '@lib/product-schema'
import { SubscriptionsResponse } from 'types/subscription'
import { EnrichedProduct } from '@lib/productCatalog'
import useToast from '@components/ui/Toast'
import Head from 'next/head'

interface ProductViewProps {
  product: Product
  story: ProductContent
}

const fetcher = (url: string) => fetch(url).then((res) => res.json())

const ProductView: FC<ProductViewProps> = ({ product, story }) => {
  const addItem = useAddItem()
  const { openSidebar } = useUI()
  const [loading, setLoading] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({})
  const htmlDescription = story
    ? Storyblok.richTextResolver.render(story.description)
    : ''
  const optionSelections = getOptionSelectionsForBC(product, selectedOptions)
  const { price } = usePriceWithOptionSelections({
    defaultPrice: product.price.value,
    productId: Number(product.id),
    optionSelections,
  })

  useEffect(() => {
    selectDefaultOptionFromProduct(product, setSelectedOptions)
    ProductSchema(product)
  }, [product])

  const variant = getProductVariant(product, selectedOptions)
  const { user } = useUser()
  const toast = useToast()
  const addToCart = async () => {
    setLoading(true)
    try {
      const cartItemBody = {
        productId: String(product.id),
        variantId: String(variant ? variant.id : product.variants[0].id),
      } as CartItemBody
      if (!!optionSelections.length) {
        // @ts-ignore
        cartItemBody['optionSelections'] = optionSelections || []
      }

      const isRx = product.sku && PHARMA_SKUS.includes(product.sku)

      if (isRx && user) {
        const alreadySubscribed = await hasAlreadySubscribed()
        if (alreadySubscribed) {
          setLoading(false)
          toast.info(
            'Cannot add to cart',
            'A subscription already exists for this product. Please contact customer support if you need assistance.'
          )
          return
        }
      }
      await addItem(cartItemBody)
      trackAddToCart(product, 1)
      openSidebar()
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  const hasAlreadySubscribed = async () => {
    const { data: subscriptionData }: { data?: SubscriptionsResponse } =
      await fetcher('/api/account/subscriptions')
    return !!subscriptionData?.products.find(
      (sp: EnrichedProduct) => sp.bcProduct.sku == product.sku
    )
  }

  const showAlbum = product.sku ? !['LFPPP0001'].includes(product.sku) : true

  return (
    <>
      <Head>
        <title>{story?.seo_headline}</title>
        <meta name="description" content={story?.seo_excerpt} />
      </Head>
      <Container className="max-w-6xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 relative inset-y-4 lg:mb-40">
          <div className="col-span-5 flex flex-col items-center m-4">
            <ImageSlider key={product.id} showAlbum={showAlbum}>
              {product.images.map((image: any, i) => (
                <InnerImageZoom
                  key={i}
                  src={image.url}
                  zoomSrc={image.url1280wide}
                  zoomType="hover"
                  hideHint
                  fullscreenOnMobile
                />
              ))}
            </ImageSlider>
          </div>
          <div className="m-6 col-span-7 py-4">
            <Typography
              variant="h5"
              component="h5"
              className="font-bold uppercase"
            >
              {story?.need_description}
            </Typography>
            <Typography
              variant="h1"
              component="h1"
              className="font-bold uppercase text-h2-m md:text-h2"
              style={{fontWeight: 800}}
            >
              {story && story?.name?.length ? story.name : product.name}
            </Typography>
            <Text
              className="pb-4 break-words w-full max-w-xl pt-3 font-normal"
              html={htmlDescription}
            />
            <ProductOptions
              options={product.options}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
            />
            <CallToAction
              story={story}
              addToCart={addToCart}
              loading={loading}
              price={price}
              frequency={getFrequency(selectedOptions)}
              availableForSale={
                product.availabilityV2.status == 'Available' &&
                (variant?.availableForSale || true)
              }
              productSku={product.sku}
            />
            {product.availabilityV2.status != 'Available' && (
              <Typography variant="body-sm" component="p" className="pt-4">
                This item is temporarily out of stock
              </Typography>
            )}
            <Typography
              variant="body-lg"
              component="p"
              className="pt-4"
              color="teal"
            >
              All subscriptions are billed monthly and may be cancelled online
              at any time.
            </Typography>
          </div>
        </div>
      </Container>
      <div className="w-full px-0">
        {story.content_bloks &&
          story.content_bloks.map((blok: StoryblokContent) => (
            <DynamicComponent blok={blok} key={blok._uid} />
          ))}
      </div>
    </>
  )
}

const getFrequency = (selectedOptions: SelectedOptions) => {
  const frequencyKey = Object.keys(selectedOptions).find((key) =>
    key.includes('frequency')
  )
  return frequencyKey ? String(selectedOptions[frequencyKey]) : 'one-time'
}

const getOptionSelectionsForBC = (
  product: Product,
  selectedOptions: SelectedOptions
) =>
  Object.entries(selectedOptions).map(
    ([productDisplayName, optionChoiceLabel]) => {
      const option = product.options.find(
        (option) => option.displayName.toLowerCase() === productDisplayName
      )
      if (!option) {
        return console.error(
          'Could not find option',
          productDisplayName,
          option
        )
      }
      const choice = option.values.find(
        (value) => value.label.toLowerCase() === optionChoiceLabel
      )
      if (!choice) {
        return console.error('Could not find choice', option, optionChoiceLabel)
      }
      return {
        option_id: option.id,
        option_value: choice.entityId,
      }
    }
  )

export default ProductView
